import React, { useCallback, useEffect, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import FloatingSlideOutSidebar from "../../../../ui-components/floating-slide-out-sidebar/floatingSlideOutSidebar";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import LabeledTextInput from "../../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSCheckbox from "../../../../ui-components/xgs-checkbox/xgsCheckbox";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import { CreateBatchResponse } from "../../../../app/data/invoice-factoring/models";
import { CSV_TEMPLATE, FACTORING_MAX_FILE_BYTES, pluralize } from "../constants";
import { downloadText } from "../common/downloadFile";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

export interface NewUploadSidebarProps {
  show: boolean;
  onClose: () => void;
  onCreated: () => void;
  onOpenBatch: (batchId: string) => void;
}

const NewUploadSidebar: React.FC<NewUploadSidebarProps> = ({ show, onClose, onCreated, onOpenBatch }) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [startNow, setStartNow] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateBatchResponse | null>(null);

  const reset = () => {
    setFile(null);
    setName("");
    setStartNow(false);
    setFileError(null);
    setResult(null);
  };

  useEffect(() => {
    if (!show) reset();
  }, [show]);

  const onDrop = useCallback((accepted: File[], rejected: FileRejection[]) => {
    if (rejected.length) {
      const code = rejected[0].errors[0]?.code;
      setFileError(
        code === "file-too-large" ? "The file must be 5 MB or smaller." : "Choose a single CSV file."
      );
      return;
    }
    setFileError(null);
    setFile(accepted[0] ?? null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"] },
    maxSize: FACTORING_MAX_FILE_BYTES,
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const createMutation = useMutation({
    mutationFn: () => invoiceFactoringService.createBatch({ file: file as File, name: name.trim() || undefined, start: startNow }),
    onSuccess: (response) => {
      setResult(response);
      onCreated();
    },
    onError: (e: Error) => toast.error(e.message || "Unable to create the batch."),
  });

  const batch = result?.batch;
  const validCount = batch ? batch.totalCount - batch.invalidCount : 0;

  return (
    <FloatingSlideOutSidebar
      storageKey="invoice-factoring-new-upload"
      header="New Upload Batch"
      show={show}
      onClose={onClose}
      size="small"
      spinner={createMutation.isLoading}
    >
      <div className="xgs-invoice-factoring__upload">
        {!batch ? (
          <>
            <p className="xgs-invoice-factoring__muted">
              Upload a CSV with one probill number per row. A <code>probill_number</code> header is optional. Probills
              already sent to Denim are recorded as already sent and are not sent again.
            </p>
            <Button
              theme={ButtonThemes.transparent}
              type="button"
              className="xgs-invoice-factoring__inline-button"
              onClick={() => downloadText(CSV_TEMPLATE, "invoice_factoring_template.csv")}
            >
              Download CSV template
            </Button>

            <div
              {...getRootProps({
                className: `xgs-invoice-factoring__dropzone${isDragActive ? " xgs-invoice-factoring__dropzone--active" : ""}`,
              })}
            >
              <input {...getInputProps()} />
              {file ? (
                <span>
                  <strong>{file.name}</strong> ({Math.ceil(file.size / 1024)} KB). Drop another file to replace it.
                </span>
              ) : (
                <span>Drag a CSV file here, or click to choose one. Up to 5 MB.</span>
              )}
            </div>
            {fileError && <div className="xgs-invoice-factoring__text--error">{fileError}</div>}

            <LabeledTextInput
              label="Batch name:"
              labelMode={LabelModes.column}
              type="text"
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="Defaults to the file name"
              className="xgs-invoice-factoring__upload-field"
            />

            <XGSCheckbox
              name="invoice-factoring-start-now"
              checked={startNow}
              onChange={(e) => setStartNow(e.target.checked)}
              mix="xgs-invoice-factoring__upload-field"
            >
              Start sending right away
            </XGSCheckbox>
            {!startNow && (
              <div className="xgs-invoice-factoring__muted">
                The batch is created as not started so you can review its rows first.
              </div>
            )}

            <div className="xgs-invoice-factoring__upload-actions">
              <Button theme={ButtonThemes.blueInverted} size={ButtonSizes.auto} type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                theme={ButtonThemes.blue}
                size={ButtonSizes.auto}
                type="button"
                disabled={!file || createMutation.isLoading}
                spinner={createMutation.isLoading}
                onClick={() => createMutation.mutate()}
              >
                {startNow ? "Upload and start" : "Upload"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="xgs-invoice-factoring__upload-result">
              <div className="xgs-invoice-factoring__upload-result-title">
                Batch #{batch.batchNumber} created {batch.status === "QUEUED" ? "and queued for sending" : "as not started"}.
              </div>
              <ul>
                <li>{pluralize(batch.totalCount, "row")} in the file</li>
                <li>{pluralize(validCount, "probill")} to send</li>
                {batch.invalidCount > 0 && <li>{pluralize(batch.invalidCount, "invalid or duplicate row")} skipped</li>}
              </ul>
            </div>
            {!!result?.warnings.length && (
              <div className="xgs-invoice-factoring__warning-box">
                {result.warnings.map((warning) => (
                  <div key={warning}>{warning}.</div>
                ))}
              </div>
            )}
            <div className="xgs-invoice-factoring__upload-actions">
              <Button theme={ButtonThemes.blueInverted} size={ButtonSizes.auto} type="button" onClick={reset}>
                Upload another
              </Button>
              <Button theme={ButtonThemes.blue} size={ButtonSizes.auto} type="button" onClick={() => onOpenBatch(batch.id)}>
                Open batch
              </Button>
            </div>
          </>
        )}
      </div>
    </FloatingSlideOutSidebar>
  );
};

export default NewUploadSidebar;
